// frontend/src/utils/channelProjection.js
//
// The one home for reading a purchase order's sales-channel projection
// state (`channel_projection_status` / `channel_projection_issues`,
// purchase_orders/models.py), so every screen that renders it offers the
// merchant the same action and the same words for the same state.
//
// Statuses (PurchaseOrderHeader.PROJECTION_*):
//   not_applicable — no projection (draft, ERP-owned, manual store).
//                    Renders nothing.
//   synced         — the PO is mirrored in Shopify as an inventory transfer.
//                    The expected state of every issued PO on a Shopify
//                    store, so it renders nothing: only the exceptions
//                    (failed, diverged) earn a chip.
//   failed         — the last projection step failed; the merchant retries
//                    (POST /project/) after fixing the cause.
//   diverged       — Shopify contradicts the PO. The PO is the record; the
//                    merchant fixes it in Shopify or by receiving / closing
//                    in Manexys. The ONE divergence with a Manexys-side
//                    action is a transfer missing in Shopify, where
//                    /project/ re-runs the projection.

export const PROJECTION_SYNCED = 'synced';
export const PROJECTION_FAILED = 'failed';
export const PROJECTION_DIVERGED = 'diverged';

// One sentence per issue code a purchase order can carry
// (`core.integration.FAILURE_REASONS`, pinned to this table by
// backend/core/integration/test_failure_reasons.py, which names the codes
// that only reach goods-receipt and inventory-push logs). `channel_rejected`
// is not here: it always carries Shopify's own words in `channel_message`,
// shown verbatim, the way the promotion price sync shows them
// (components/promotions/priceSyncReason.js).
export const PROJECTION_ISSUE_KEYS = {
  channel_rate_limited: 'purchaseOrders.projection.issue.channelRateLimited',
  channel_unreachable: 'purchaseOrders.projection.issue.channelUnreachable',
  channel_refused: 'purchaseOrders.projection.issue.channelRefused',
  channel_request_failed: 'purchaseOrders.projection.issue.channelRequestFailed',
  channel_scope_missing: 'purchaseOrders.projection.issue.channelScopeMissing',
  item_not_linked: 'purchaseOrders.projection.issue.itemNotLinked',
  item_not_tracked: 'purchaseOrders.projection.issue.itemNotTracked',
  location_not_linked: 'purchaseOrders.projection.issue.locationNotLinked',
  too_many_items: 'purchaseOrders.projection.issue.tooManyItems',
  adoption_ambiguous: 'purchaseOrders.projection.issue.adoptionAmbiguous',
  adoption_mismatch: 'purchaseOrders.projection.issue.adoptionMismatch',
  adoption_tag_moved: 'purchaseOrders.projection.issue.adoptionTagMoved',
  adopted_transfer_missing: 'purchaseOrders.projection.issue.adoptedTransferMissing',
  answer_truncated: 'purchaseOrders.projection.issue.answerTruncated',
  transfer_too_long: 'purchaseOrders.projection.issue.transferTooLong',
  several_shipments: 'purchaseOrders.projection.issue.severalShipments',
  mirror_gone: 'purchaseOrders.projection.issue.mirrorGone',
  shipment_line_unmatched: 'purchaseOrders.projection.issue.shipmentLineUnmatched',
  transfer_tagged_twice: 'purchaseOrders.projection.issue.transferTaggedTwice',
  transfer_missing: 'purchaseOrders.projection.issue.transferMissing',
  transfer_cancelled: 'purchaseOrders.projection.issue.transferCancelled',
  no_transfer_line: 'purchaseOrders.projection.issue.noTransferLine',
  line_not_on_po: 'purchaseOrders.projection.issue.lineNotOnPo',
  transfer_line_differs: 'purchaseOrders.projection.issue.transferLineDiffers',
  shipment_line_missing: 'purchaseOrders.projection.issue.shipmentLineMissing',
  rejected_on_open_po: 'purchaseOrders.projection.issue.rejectedOnOpenPo',
  accepted_below_ledger: 'purchaseOrders.projection.issue.acceptedBelowLedger',
  accepted_above_ordered: 'purchaseOrders.projection.issue.acceptedAboveOrdered',
  received_not_in_ledger: 'purchaseOrders.projection.issue.receivedNotInLedger',
};

/**
 * One issue as the PO screen says it: `<identifier>: <why>`, where why is
 * Shopify's own words when it gave some, else the sentence for the code.
 *
 * @param {{identifier: string, reason: string, params: object,
 *          channel_message: string|null}} issue
 *   one entry of `channel_projection_issues`
 * @param {Function} t
 * @returns {string}
 */
export function projectionIssueText(issue, t) {
  if (issue.channel_message != null) return `${issue.identifier}: ${issue.channel_message}`;
  const key = PROJECTION_ISSUE_KEYS[issue.reason];
  if (!key) {
    throw new Error(`channel projection: unknown issue reason ${JSON.stringify(issue.reason)}`);
  }
  return `${issue.identifier}: ${t(key, issue.params)}`;
}

/**
 * Whether the projection's transfer no longer exists in Shopify — the
 * `transfer_missing` issue `projection_reconcile.reconcile_po` writes, the
 * only "diverged" the merchant can act on from Manexys.
 */
export function isTransferMissing(issues) {
  return issues.some((issue) => issue.reason === 'transfer_missing');
}

/**
 * The action the merchant is offered for a projection state:
 *   'retry'     — failed: re-run the projection.
 *   'reproject' — diverged because the transfer is gone: recreate it.
 *   null        — nothing to do from Manexys (synced, not applicable, or a
 *                 divergence that is settled in Shopify or by the PO's own
 *                 lifecycle).
 *
 * `issues` is the detail-only `channel_projection_issues`.
 */
export function projectionAction(status, issues) {
  if (status === PROJECTION_FAILED) return 'retry';
  if (status === PROJECTION_DIVERGED && isTransferMissing(issues)) return 'reproject';
  return null;
}

/**
 * Whether the projection state is one the merchant must be told about.
 * Only these render anything. A synced PO is the norm, and a "Shown in
 * Shopify" chip on every row would say nothing.
 */
export function isProjectionException(status) {
  return status === PROJECTION_FAILED || status === PROJECTION_DIVERGED;
}
